/* eslint-disable @reatom/unit-naming-rule */
import { atom } from '@reatom/framework'
import { withLocalStorage } from '@reatom/persist-web-storage'
import { match } from 'ts-pattern'

import { PoolEvent } from '@blade/blockchain-types'
import { Result } from '@blade/std-lib'
import {
  fetchCookingPairsRequestAction,
  fetchFinalStretchPairsRequestAction,
  fetchGraduatedPairsRequestAction,
  fetchTopGainersRequestAction,
  pairRegistry,
  reatomPair,
} from '@root/entities/pair/atoms'
import { STORAGE_PREFIX } from '@root/shared'

import { ExploreFilter } from '../model'
import { EXPLORE_LIST_PERIOD_MAP } from '../model/constants'
import { ExploreFeedType } from '../model/explore-feed-type'

import { reatomPairEventsCommon } from './reatom-explore-events'
import {
  cookingExploreFiltersAtom,
  finalStretchExploreFiltersAtom,
  graduatedExploreFiltersAtom,
  topGainersExploreFiltersAtom,
} from './reatom-explore-filters'

import type { Ctx } from '@reatom/framework'
import type { PairMetrics } from '@root/entities/pair/model'

export type ExploreSortKey = ExploreFilter.PoolsSortBy

const DEFAULT_TOP_GAINERS_SORTING: SpecialPairsSorting = {
  sortBy: 'marketCapUsd',
  sortDir: 'desc',
}

export type SpecialPairsSorting = {
  sortBy: ExploreSortKey
  sortDir: 'asc' | 'desc'
}

export const SPECIAL_PAIRS_SORT_KEYS = [
  'holdersCount',
  'txCount',
  'createdAt',
  'marketCapUsd',
] as const satisfies Array<ExploreSortKey>

export const topGainersSortByAtom = atom<SpecialPairsSorting>(
  DEFAULT_TOP_GAINERS_SORTING,
  'topGainersSortBy',
).pipe(withLocalStorage(`${STORAGE_PREFIX}/v1/explore/top-gainers-sorting`))

export const cookingSortByAtom = atom<SpecialPairsSorting>(
  DEFAULT_TOP_GAINERS_SORTING,
  'cookingSortBy',
).pipe(withLocalStorage(`${STORAGE_PREFIX}/v1/explore/cooking-sorting`))

/**
 * Newest graduation first, which is the only order this column has.
 *
 * It used to borrow the gainers' default and rank by market cap, so the
 * column answered "the biggest things that have graduated in three days"
 * rather than "what just graduated" - pools forty minutes to two hours old,
 * largest first, while the ones that had just migrated sat below them. The
 * server already returns them newest-first; the client was overriding it.
 *
 * `createdAt` is the migration: a graduated pool is the AMM pool the
 * migration creates, so the two timestamps are the same moment. Measured on
 * sixty live rows, `initTimestamp` equalled `migration.migrationTimestamp` on
 * every one. Sorting by creation keeps the key inside the set the sort menu
 * offers, which the migration key is not in.
 *
 * The storage key moves to v2 on purpose. The old default is persisted in
 * every browser that has opened this column, and a new default that loses to
 * a stale copy of the old one would change nothing for anybody who has
 * already been here.
 */
export const graduatedSortByAtom = atom<SpecialPairsSorting>(
  { sortBy: 'createdAt', sortDir: 'desc' },
  'graduatedSortBy',
).pipe(withLocalStorage(`${STORAGE_PREFIX}/v2/explore/graduated-sorting`))

export type ExploreTopGainersTab =
  | typeof ExploreFeedType.TopGainers
  | typeof ExploreFeedType.Cooking

const getSortingOrder = (
  ctx: Ctx,
  sorting: SpecialPairsSorting,
  period: PairMetrics.MetricsSpan,
) => {
  return match(sorting)
    .with({ sortBy: 'marketCapUsd' }, ({ sortDir }) =>
      sortDir === 'asc'
        ? reatomPair.cmpByMarketCapAsc(ctx)
        : reatomPair.cmpByMarketCapDesc(ctx),
    )
    .with({ sortBy: 'txCount' }, ({ sortDir }) =>
      sortDir === 'asc'
        ? reatomPair.cmpByTxCountAsc(period)(ctx)
        : reatomPair.cmpByTxCountDesc(period)(ctx),
    )
    .with({ sortBy: 'createdAt' }, ({ sortDir }) =>
      sortDir === 'asc'
        ? reatomPair.cmpByCreatedAtAsc
        : reatomPair.cmpByCreatedAtDesc,
    )
    .with({ sortBy: 'holdersCount' }, ({ sortDir }) =>
      sortDir === 'asc'
        ? reatomPair.cmpByHoldersCountAsc(ctx)
        : reatomPair.cmpByHoldersCountDesc(ctx),
    )
    .otherwise(() => undefined)
}

const reatomExploreTopGainers = () => {
  const pairsStateAtom = reatomPairEventsCommon({
    event: PoolEvent.TopGainer,
    filter: topGainersExploreFiltersAtom,
    period: EXPLORE_LIST_PERIOD_MAP[ExploreFeedType.TopGainers],
    compare: (ctx) => {
      const sorting = ctx.get(topGainersSortByAtom)
      return getSortingOrder(
        ctx,
        sorting,
        EXPLORE_LIST_PERIOD_MAP[ExploreFeedType.TopGainers],
      )
    },
    loadItems: async (ctx, { limit, filter, network, period }) => {
      const sorting = ctx.spy(topGainersSortByAtom)

      const result = await ctx.schedule(() =>
        fetchTopGainersRequestAction(ctx, {
          offset: 0,
          limit,
          filters: filter
            ? ExploreFilter.toPoolFiltersDto(
                filter,
                { sortBy: sorting.sortBy, sortDir: sorting.sortDir },
                period,
              )
            : undefined,
          network,
        }),
      )

      return result.pipe(
        Result.map((it) =>
          it.data.map((dto) => pairRegistry.resolveFromDto(ctx, dto)),
        ),
      )
    },
  })

  return pairsStateAtom
}

export type ExploreTopGainersModel = ReturnType<typeof reatomExploreTopGainers>
export const exploreTopGainersModel = reatomExploreTopGainers()

const reatomExploreCooking = () => {
  const pairsStateAtom = reatomPairEventsCommon({
    event: PoolEvent.Cooking,
    filter: cookingExploreFiltersAtom,
    period: EXPLORE_LIST_PERIOD_MAP[ExploreFeedType.Cooking],
    compare: (ctx) => {
      const sorting = ctx.get(cookingSortByAtom)
      return getSortingOrder(
        ctx,
        sorting,
        EXPLORE_LIST_PERIOD_MAP[ExploreFeedType.Cooking],
      )
    },
    loadItems: async (ctx, { limit, filter, network, period }) => {
      const sorting = ctx.spy(cookingSortByAtom)

      const result = await ctx.schedule(() =>
        fetchCookingPairsRequestAction(ctx, {
          offset: 0,
          filters: filter
            ? ExploreFilter.toPoolFiltersDto(
                filter,
                { sortBy: sorting.sortBy, sortDir: sorting.sortDir },
                period,
              )
            : undefined,
          limit,
          network,
        }),
      )

      return result.pipe(
        Result.map((it) =>
          it.data.map((dto) => pairRegistry.resolveFromDto(ctx, dto)),
        ),
      )
    },
  })

  return pairsStateAtom
}
export const exploreCookingModel = reatomExploreCooking()

/**
 * Pools still climbing a bonding curve, closest to graduating first.
 *
 * No `compare`: the server already returns them in curve order, and there is
 * nothing on this screen to re-sort them by that would be truer.
 */
const reatomExploreFinalStretch = () => {
  const pairsStateAtom = reatomPairEventsCommon({
    event: PoolEvent.FinalStretch,
    filter: finalStretchExploreFiltersAtom,
    period: EXPLORE_LIST_PERIOD_MAP[ExploreFeedType.FinalStretch],
    loadItems: async (ctx, { limit, filter, network, period }) => {
      const result = await ctx.schedule(() =>
        fetchFinalStretchPairsRequestAction(ctx, {
          offset: 0,
          filters: filter
            ? ExploreFilter.toPoolFiltersDto(filter, undefined, period)
            : undefined,
          limit,
          network,
        }),
      )

      return result.pipe(
        Result.map((it) =>
          it.data.map((dto) => pairRegistry.resolveFromDto(ctx, dto)),
        ),
      )
    },
  })

  return pairsStateAtom
}
export const exploreFinalStretchModel = reatomExploreFinalStretch()

const reatomExploreGraduated = () => {
  const pairsStateAtom = reatomPairEventsCommon({
    event: PoolEvent.Graduated,
    filter: graduatedExploreFiltersAtom,
    period: EXPLORE_LIST_PERIOD_MAP[ExploreFeedType.Graduated],
    /*
     * The same reason New Pairs has one: a page arrives sorted, and then
     * graduations arrive one at a time and are put wherever they land.
     * Arrival is not order, so without this the top of the column drifts out
     * of sequence the longer it is left open.
     */
    compare: (ctx) =>
      getSortingOrder(
        ctx,
        ctx.get(graduatedSortByAtom),
        EXPLORE_LIST_PERIOD_MAP[ExploreFeedType.Graduated],
      ),
    loadItems: async (ctx, { limit, filter, network, period }) => {
      const sorting = ctx.spy(graduatedSortByAtom)

      const result = await ctx.schedule(() =>
        fetchGraduatedPairsRequestAction(ctx, {
          offset: 0,
          limit,
          filters: filter
            ? ExploreFilter.toPoolFiltersDto(
                filter,
                { sortBy: sorting.sortBy, sortDir: sorting.sortDir },
                period,
              )
            : undefined,
          network,
        }),
      )

      return result.pipe(
        Result.map((it) =>
          it.data.map((dto) => pairRegistry.resolveFromDto(ctx, dto)),
        ),
      )
    },
  })

  return pairsStateAtom
}
export const exploreGraduatedModel = reatomExploreGraduated()
